/**
 * PaymentFlow — the flow section of the payment detail page, laid out as the
 * payment travels: the outbound lane, the wait at the bank, then the inbound
 * lane holding one diagram per bank response type. The bank is time spent
 * rather than a system we observe, so it sits between the lanes with an arrow
 * in and an arrow out; the idle gap between two response types reads the same
 * way. Redux-connected; renders nothing without graphs.
 */
import { Fragment, useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import { useAppSelector } from 'src/store/hooks';
import { FlowGraph } from 'src/store/slices/paymentDetail/paymentDetailTypes';
import { FlowDiagram } from './FlowDiagram';
import { FlowNodeDetails } from './FlowNodeDetails';
import './flow.css';

const Chips = ({ bottleneck, latency }: { bottleneck?: string; latency?: string }) => (
  <span className="flow-chips">
    {bottleneck && (
      <span className="flow-chip flow-chip--warning">
        <span aria-hidden="true">⚠</span> Slowest: {bottleneck}
      </span>
    )}
    {latency && <span className="flow-chip">{latency}</span>}
  </span>
);

/** One graph plus the detail for whichever of its systems is selected. */
const Graph = ({ graph, caption }: { graph: FlowGraph; caption?: string }) => {
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const selected = graph.nodes.find(node => node.id === selectedId);

  return (
    <section className="flow-block" aria-label={`${caption ?? graph.title} flow`}>
      <header className="flow-block__header">
        {caption && <span className="flow-block__caption">{caption}</span>}
        <Chips bottleneck={graph.bottleneckLabel} latency={graph.totalLatency} />
      </header>

      <div className="flow-block__body">
        <div className="flow-block__canvas">
          <FlowDiagram
            graph={graph}
            selectedNodeId={selectedId}
            onSelectNode={id => setSelectedId(current => (current === id ? undefined : id))}
          />
          {!selected && <p className="flow-block__hint">Select a system to see its phases.</p>}
        </div>
        {selected && (
          <aside className="flow-block__detail">
            <FlowNodeDetails node={selected} onClose={() => setSelectedId(undefined)} />
          </aside>
        )}
      </div>

      {graph.dropped.length > 0 && (
        <details className="flow-dropped">
          <summary>{graph.dropped.length} event(s) not placed on this diagram</summary>
          <ul>
            {graph.dropped.map(event => (
              <li key={event.id}>{event.system} · {event.status} · {event.at}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
};

/** Time between two blocks. Label is optional: a response-type gap is just a duration. */
const Transit = ({ label, value }: { label?: string; value: string }) => (
  <div className="flow-transit">
    <span className="flow-transit__arrow" aria-hidden="true" />
    <div className="flow-transit__box">
      {label && <span className="flow-transit__label">{label}</span>}
      <span className="flow-transit__value">{value}</span>
    </div>
    <span className="flow-transit__arrow" aria-hidden="true" />
  </div>
);

const Lane = ({ label, graphs }: { label: string; graphs: FlowGraph[] }) => (
  <section className={`flow-lane flow-lane--${label.toLowerCase()}`} aria-label={`${label} flow`}>
    <h3 className="flow-lane__label">{label}</h3>
    {graphs.map(graph => (
      <Fragment key={graph.id}>
        {graph.gapFromPrevious && <Transit value={graph.gapFromPrevious} />}
        <Graph graph={graph} caption={graphs.length > 1 ? graph.title : undefined} />
      </Fragment>
    ))}
  </section>
);

export const PaymentFlow = () => {
  const detail = useAppSelector(state => state.paymentDetail.data);
  if (!detail) return null;

  const { outbound, inbound, bankWait } = detail.flow;
  if (outbound.length === 0 && inbound.length === 0) return null;

  return (
    <div className="flow-root">
      <Container
        header={
          <Header
            variant="h2"
            actions={<Chips
              bottleneck={detail.bottleneckLabel}
              latency={detail.totalLatency && `${detail.totalLatency} end to end`}
            />}
          >
            Flow
          </Header>
        }
      >
        <div className="flow">
          {outbound.length > 0 && <Lane label="Outbound" graphs={outbound} />}
          {bankWait && outbound.length > 0 && inbound.length > 0 && (
            <Transit label="Waiting for bank" value={bankWait} />
          )}
          {inbound.length > 0 && <Lane label="Inbound" graphs={inbound} />}
        </div>
      </Container>
    </div>
  );
};
